import { Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { environment } from '../../../../environments/environment';
import { AlertService } from '../../shared/alert/alert.service';
import { CategoryModel, DEFAULT_CATEGORIES, FINANCE_GROUPS, FinanceGroup } from '../finance.model';
import { FinanceStoreService } from '../finance-store.service';
import { CategoryDialogComponent } from './category-dialog.component';

@Component({
  selector: 'finance-categories',
  standalone: false,
  templateUrl: './categories.component.html',
  styleUrl: '../finances-list.scss'
})
export class CategoriesComponent implements OnInit {
  icons = environment.icons;
  groups = FINANCE_GROUPS;
  categories: CategoryModel[] = [];
  columns = ['name', 'active', 'action'];

  private store = inject(FinanceStoreService);
  private dialog = inject(MatDialog);
  private alert = inject(AlertService);

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async load(): Promise<void> {
    const categories = await this.store.list<CategoryModel>('FinanceCategories');
    this.categories = categories.sort((a, b) => a.name.localeCompare(b.name));
  }

  byGroup(group: FinanceGroup): CategoryModel[] {
    return this.categories.filter(c => c.group == group);
  }

  edit(category?: CategoryModel): void {
    this.dialog.open(CategoryDialogComponent, { data: { ...category } }).afterClosed().subscribe(async (result?: CategoryModel) => {
      if (!result) return;

      const duplicated = this.categories.some(c => c.id != result.id && c.group == result.group && c.name.toLowerCase() == result.name.toLowerCase());
      if (duplicated) {
        this.alert.show('Category already exists!', 'error');
        return;
      }

      try {
        if (result.id) await this.store.update('FinanceCategories', result);
        else await this.store.add('FinanceCategories', result);
        this.alert.show('Saved!', 'save_as');
        await this.load();
      } catch {
        this.alert.show('Save failed!', 'error');
      }
    });
  }

  async delete(category: CategoryModel): Promise<void> {
    try {
      await this.store.remove('FinanceCategories', category.id);
      this.alert.show('Deleted!', 'delete_forever');
      await this.load();
    } catch {
      this.alert.show('Delete failed!', 'error');
    }
  }

  // Starting point based on the spreadsheet; only creates what is missing
  async createDefaults(): Promise<void> {
    const missing = DEFAULT_CATEGORIES.filter(d => !this.categories.some(c => c.group == d.group && c.name.toLowerCase() == d.name.toLowerCase()));
    try {
      for (const category of missing)
        await this.store.add('FinanceCategories', { ...category, active: true });
      this.alert.show(`${missing.length} categories created!`, 'save_as');
      await this.load();
    } catch {
      this.alert.show('Create failed!', 'error');
    }
  }
}
